"use client";

import { PostContact } from "@/actions/contact/post-contact";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { mainPageContactConfig } from "@/config/main/pages";
import { LoadingDots } from "@/icons";
import { contactSchema } from "@/lib/validation/contact";
import { zodResolver } from "@hookform/resolvers/zod";
import { Send } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-hot-toast";
import * as z from "zod";

type ContactFormValues = z.infer<typeof contactSchema>;

const defaultValues: Partial<ContactFormValues> = {
  name: "",
  email: "",
  message: "",
};

const MainContactPage = () => {
  const [isLoading, setIsLoading] = useState(false);

  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactSchema),
    defaultValues,
  });

  async function onSubmit(data: ContactFormValues) {
    setIsLoading(true);
    const response = await PostContact({
      message: data.message,
      name: data.name,
      email: data.email,
    });
    if (!response) {
      toast.error(mainPageContactConfig.error);
    } else {
      toast.success(mainPageContactConfig.emailSent);
    }
    setIsLoading(false);
    form.reset();
  }

  return (
    <>
      <div className="mx-auto max-w-2xl text-center">
        <h2 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl">
          Contact Us
        </h2>
        <p className="mt-2 text-lg leading-8 text-muted-foreground">
          Get in touch with us anytime.
        </p>
      </div>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="mx-auto my-10 space-y-4 text-center"
        >
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-muted-foreground">
                  {mainPageContactConfig.name}
                </FormLabel>
                <div className="mx-auto flex w-full max-w-md space-x-2">
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-muted-foreground">
                  {mainPageContactConfig.email}
                </FormLabel>
                <div className="mx-auto flex w-full max-w-md space-x-2">
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="message"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-muted-foreground">
                  {mainPageContactConfig.message}
                </FormLabel>
                <div className="mx-auto flex w-full max-w-md space-x-2 bg-white">
                  <FormControl>
                    <Textarea className="resize-none" {...field} />
                  </FormControl>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button
            type="submit"
            className="w-full max-w-md items-center justify-center rounded-lg"
          >
            {isLoading ? (
              <LoadingDots />
            ) : (
              <>
                <Send className="mr-2" />
                {mainPageContactConfig.send}
              </>
            )}
          </Button>
        </form>
      </Form>
    </>
  );
};

export default MainContactPage;
